import {FC} from 'react'
import {Trans, useTranslation} from '@/translations/i18n.tsx'
import {errorHandler} from '@utilities/helpers.ts'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient.ts'
import {toast} from '@utilities/toast/toast.tsx'
import {DeleteModal} from '@/components/commons/delete-modal/DeleteModal'
import {ProjectIndex} from '@/features/projects/types.ts'
import {useDeleteProject} from '@/features/projects/queries/useDeleteProject'
import {formatDate} from '@/dayjs'

interface DeleteProjectModalProps {
    onClose: () => void
    onSuccess?: () => void
    project: ProjectIndex
}
export const DeleteProjectModal: FC<DeleteProjectModalProps> = ({project, onClose, onSuccess}) => {
    const {t} = useTranslation()
    const queryClient = useQueryClient()
    const deleteMutation = useDeleteProject(project.id, {
        onError: errorHandler,
        onSuccess: async () => {
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECTS]})
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECT, project.id]})
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.TREATMENT_REQUESTS]})
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.PATIENTS]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.TREATMENT_REQUESTS]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS]})
            toast.success(
                t('commons:delete_success', {
                    fullName: t('projects:code_date', {
                        code: project.code,
                        date: project.startDate ? formatDate(project.startDate, 'DD/MM/YYYY') : '-'
                    })
                })
            )
            onSuccess?.()
            onClose()
        }
    })

    return (
        <DeleteModal
            onClose={onClose}
            deleteMutation={deleteMutation}
            canBeDeleted
            fullName={`${t('commons:pri_n', {number: project.code})} - ${project.patient.fullName}`}
            paragraph1={t('commons:delete_paragraph_1')}
            title={t('projects:delete_project_title')}
            deniedTitle={t('projects:cannot_delete_project_title')}
            deniedParagraph={
                <Trans
                    i18nKey="commons:cannot_delete_paragraph"
                    values={{fullName: project.patient.fullName}}
                    components={{b: <b />}}
                />
            }
        />
    )
}
